import { Alert, MenuItem, TextField, Typography } from "@mui/material";
import { SafeDateTimePicker } from "components/SafeDateTimePicker";
import dayjs from "dayjs";
import { useState } from "react";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  SensorTopology,
  SensorTopologyChanges,
  ScrapEyeRecordingPosition,
  SensorType,
  useApplySensorTopologyChangesMutation,
  useCreateSensorMutation,
  useProvisionNewManualDeviceMutation,
  useListSensorCameraRecordingsQuery,
} from "src/store/api/generatedApi";
import { SensorEditorDialog } from "./SensorEditorDialog";
import {
  endConnection,
  endInstallation,
  endRelationships,
  TopologyEdit,
} from "./topologyEditing";
import {
  SELECTABLE_SENSOR_TYPES,
  SENSOR_TYPE_LABEL_KEYS,
} from "./sensorTypeLabels";
import {
  formatInstallationTimestamp,
  installationInstant,
} from "./installationTimestamp";

interface SensorTopologyChangeDialogProps {
  edit: TopologyEdit;
  topology: SensorTopology;
  positions: ScrapEyeRecordingPosition[];
  onClose: () => void;
}

export const SensorTopologyChangeDialog = ({
  edit,
  topology,
  positions,
  onClose,
}: SensorTopologyChangeDialogProps) => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const [action, setAction] = useState<"replace" | "end">("replace");
  const [at, setAt] = useState(topology.at);
  const [validTime, setValidTime] = useState(true);
  const [deviceId, setDeviceId] = useState<number | null>(
    edit.kind === "connect" || edit.kind === "reconnect" ? edit.deviceId : null
  );
  const [positionId, setPositionId] = useState<number | null>(
    edit.kind === "install" || edit.kind === "move" ? edit.positionId : null
  );
  const [port, setPort] = useState(
    edit.kind === "reconnect" && edit.deviceId === edit.connection.device_id
      ? (edit.connection.device_sensor_id ?? "")
      : ""
  );
  const [cameraId, setCameraId] = useState<number | null>(
    edit.kind === "reconnect" && edit.deviceId === edit.connection.device_id
      ? edit.connection.camera_id
      : null
  );
  const [name, setName] = useState("");
  const [serial, setSerial] = useState("");
  const [sensorType, setSensorType] = useState<SensorType>("camera");
  const [apply, applyStatus] = useApplySensorTopologyChangesMutation();
  const [createSensor, sensorStatus] = useCreateSensorMutation();
  const [createDevice, deviceStatus] = useProvisionNewManualDeviceMutation();
  const busy =
    applyStatus.isLoading || sensorStatus.isLoading || deviceStatus.isLoading;
  const sensorId =
    edit.kind === "move"
      ? edit.installation.sensor_id
      : edit.kind === "reconnect"
        ? edit.connection.sensor_id
        : edit.kind === "connect" || edit.kind === "install"
          ? edit.sensorId
          : null;
  const sensor = topology.sensors.find(
    (candidate) => candidate.id === sensorId
  );
  const connecting =
    action !== "end" && (edit.kind === "connect" || edit.kind === "reconnect");
  const installing =
    action !== "end" && (edit.kind === "install" || edit.kind === "move");
  const { data: recordings } = useListSensorCameraRecordingsQuery(
    { tenantName, deviceId: deviceId ?? 0 },
    {
      skip: !connecting || deviceId == null || sensor?.sensor_type !== "camera",
    }
  );
  const previousInstallation = edit.kind === "move" ? edit.installation : null;
  const endingConnections =
    edit.kind === "end"
      ? edit.connections
      : edit.kind === "reconnect"
        ? [edit.connection]
        : [];
  const endingInstallations =
    edit.kind === "end"
      ? edit.installations
      : (edit.kind === "install" || edit.kind === "move") &&
          previousInstallation != null
        ? [previousInstallation]
        : [];
  const endIsValid = [
    ...endingConnections.map((connection) => ({
      start: connection.connected_at,
      end: connection.disconnected_at,
    })),
    ...endingInstallations.map((installation) => ({
      start: installation.installed_at,
      end: installation.removed_at,
    })),
  ].every(
    (interval) =>
      installationInstant(at) > installationInstant(interval.start) &&
      (interval.end == null ||
        installationInstant(at) < installationInstant(interval.end))
  );
  const destinationChanged =
    action === "end" ||
    (edit.kind === "move"
      ? positionId !== edit.installation.recording_position_id
      : edit.kind === "reconnect"
        ? deviceId !== edit.connection.device_id ||
          (port.trim() || null) !== edit.connection.device_sensor_id ||
          cameraId !== edit.connection.camera_id
        : true);
  const valid =
    edit.kind === "sensor" || edit.kind === "device"
      ? name.trim().length > 0
      : destinationChanged &&
        validTime &&
        endIsValid &&
        (!connecting || deviceId != null) &&
        (!installing || positionId != null) &&
        (edit.kind !== "end" ||
          endingConnections.length + endingInstallations.length > 0);
  const title = t(
    {
      connect: "sensorConnectButton",
      reconnect: "topologyReconnect",
      install: "sensorInstallButton",
      move: "topologyMoveInstallation",
      end: "topologyEndRelationships",
      sensor: "topologyAddSensor",
      device: "topologyAddDevice",
    }[edit.kind]
  );
  const save = async () => {
    if (edit.kind === "sensor") {
      await createSensor({
        tenantName,
        scrapEyeSensorCreate: {
          name: name.trim(),
          serial_number: serial.trim() || null,
          sensor_type: sensorType,
        },
      }).unwrap();
      return;
    }
    if (edit.kind === "device") {
      await createDevice({ tenantName, name: name.trim() }).unwrap();
      return;
    }
    const connections: SensorTopologyChanges["connections"] =
      connecting && sensorId != null && deviceId != null
        ? [
            ...(edit.kind === "reconnect"
              ? [endConnection(edit.connection, at)]
              : []),
            {
              expected_version_id: null,
              connection: {
                sensor_id: sensorId,
                device_id: deviceId,
                device_sensor_id: port.trim() || null,
                camera_id: cameraId,
                connected_at: at,
                disconnected_at:
                  edit.kind === "reconnect"
                    ? edit.connection.disconnected_at
                    : null,
              },
            },
          ]
        : [];
    const installations: SensorTopologyChanges["installations"] =
      installing && positionId != null && sensorId != null
        ? [
            ...(previousInstallation != null
              ? [endInstallation(previousInstallation, at)]
              : []),
            {
              expected_version_id: null,
              installation: {
                sensor_id: sensorId,
                recording_position_id: positionId,
                priority: previousInstallation?.priority ?? 1,
                installed_at: at,
                removed_at: previousInstallation?.removed_at ?? null,
              },
            },
          ]
        : [];
    const changes =
      edit.kind === "end" || action === "end"
        ? endRelationships(
            {
              kind: "end",
              connections: endingConnections,
              installations: endingInstallations,
            },
            at
          )
        : { connections, installations };
    await apply({ tenantName, sensorTopologyChanges: changes }).unwrap();
  };
  return (
    <SensorEditorDialog
      title={title}
      busy={busy}
      valid={valid}
      onClose={onClose}
      onSave={save}
      errorMessage={t("topologySaveError")}
    >
      {(edit.kind === "reconnect" || edit.kind === "move") && (
        <TextField
          select
          label={t("actions")}
          value={action}
          disabled={busy}
          onChange={(event) => {
            if (
              event.target.value === "replace" ||
              event.target.value === "end"
            )
              setAction(event.target.value);
          }}
        >
          <MenuItem value="replace">
            {t(
              edit.kind === "reconnect"
                ? "topologyReconnect"
                : "topologyMoveInstallation"
            )}
          </MenuItem>
          <MenuItem value="end">
            {t(
              edit.kind === "reconnect"
                ? "topologyDisconnect"
                : "topologyRemoveInstallation"
            )}
          </MenuItem>
        </TextField>
      )}
      {sensor != null && (
        <Typography variant="subtitle1">{sensor.name}</Typography>
      )}
      {edit.kind === "sensor" || edit.kind === "device" ? (
        <>
          <TextField
            label={t(
              edit.kind === "device"
                ? "installationDeviceLabel"
                : "sensorNameLabel"
            )}
            value={name}
            disabled={busy}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          {edit.kind === "sensor" && (
            <>
              <TextField
                select
                label={t("sensorTypeLabel")}
                value={sensorType}
                disabled={busy}
                onChange={(event) => {
                  setSensorType(event.target.value as SensorType);
                }}
              >
                {SELECTABLE_SENSOR_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(SENSOR_TYPE_LABEL_KEYS[type])}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t("serialNumber")}
                value={serial}
                disabled={busy}
                onChange={(event) => {
                  setSerial(event.target.value);
                }}
              />
            </>
          )}
        </>
      ) : (
        <>
          <SafeDateTimePicker
            label={t("topologyEffectiveTime")}
            timezone="UTC"
            ampm={false}
            value={dayjs.utc(at)}
            disabled={busy}
            onInvalidDate={() => {
              setValidTime(false);
            }}
            onChange={(time) => {
              setValidTime(time?.isValid() ?? false);
              if (time?.isValid()) setAt(time.toISOString());
            }}
          />
          {!endIsValid && (
            <Alert severity="warning">{t("topologyEndTimeError")}</Alert>
          )}
          {endingConnections.map((connection) => (
            <Typography key={connection.id} variant="body2">
              {t("topologyEndConnectionPreview", {
                sensor: connection.sensor_name,
                device: connection.device_name,
                time: formatInstallationTimestamp(at),
              })}
            </Typography>
          ))}
          {endingInstallations.map((installation) => (
            <Typography key={installation.id} variant="body2">
              {t("topologyEndInstallationPreview", {
                sensor: installation.sensor_name,
                position: `${installation.location_name} / ${installation.recording_position_name}`,
                time: formatInstallationTimestamp(at),
              })}
            </Typography>
          ))}
          {connecting && (
            <>
              <TextField
                select
                label={t("installationDeviceLabel")}
                value={deviceId ?? ""}
                disabled={busy}
                onChange={(event) => {
                  setDeviceId(Number(event.target.value));
                  setCameraId(null);
                  setPort("");
                }}
              >
                {topology.devices.map((device) => (
                  <MenuItem key={device.id} value={device.id}>
                    {device.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t("sensorConnectionPortLabel")}
                value={port}
                disabled={busy || cameraId != null}
                onChange={(event) => {
                  setPort(event.target.value);
                }}
              />
              {sensor?.sensor_type === "camera" && (
                <TextField
                  select
                  label={t("sensorCameraFootageLabel")}
                  value={cameraId ?? ""}
                  disabled={busy || deviceId == null}
                  onChange={(event) => {
                    const identifier =
                      event.target.value === ""
                        ? null
                        : Number(event.target.value);
                    setCameraId(identifier);
                    const recording = recordings?.items.find(
                      (candidate) => candidate.id === identifier
                    );
                    if (recording != null) setPort(recording.physical_id);
                  }}
                >
                  <MenuItem value="">{t("sensorNoCameraFootage")}</MenuItem>
                  {recordings?.items.map((recording) => (
                    <MenuItem key={recording.id} value={recording.id}>
                      {recording.physical_id} —{" "}
                      {formatInstallationTimestamp(recording.created_at)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <Typography variant="body2">
                {t("topologyNewConnectionPreview", {
                  time: formatInstallationTimestamp(at),
                })}
              </Typography>
            </>
          )}
          {installing && (
            <TextField
              select
              label={t("recordingPositionNameLabel")}
              value={positionId ?? ""}
              disabled={busy}
              onChange={(event) => {
                setPositionId(Number(event.target.value));
              }}
            >
              {positions.map((position) => (
                <MenuItem key={position.id} value={position.id}>
                  {position.location_name} / {position.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </>
      )}
    </SensorEditorDialog>
  );
};
